import { motion } from "framer-motion";
import { Sparkles, Scissors } from "lucide-react";
import { IMAGES } from "../data/content";
import { useT } from "../i18n/LanguageContext";

export default function About() {
  const { t } = useT();

  const profiles = [
    {
      icon: Sparkles,
      role: t("about.profile_beauty.role"),
      name: t("about.profile_beauty.name"),
      text: t("about.profile_beauty.text"),
      testid: "profile-beauty",
    },
    {
      icon: Scissors,
      role: t("about.profile_hair.role"),
      name: t("about.profile_hair.name"),
      text: t("about.profile_hair.text"),
      testid: "profile-hair",
    },
  ];

  const pillars = [
    { no: "01", title: t("about.pillar_1.title"), text: t("about.pillar_1.text") },
    { no: "02", title: t("about.pillar_2.title"), text: t("about.pillar_2.text") },
    { no: "03", title: t("about.pillar_3.title"), text: t("about.pillar_3.text") },
  ];

  return (
    <section
      id="ueber-uns"
      data-testid="about-section"
      className="relative py-28 sm:py-40 bg-[#080808] overflow-hidden"
    >
      <div className="absolute inset-0 opacity-20">
        <img src={IMAGES.texture} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <span className="overline">{t("about.overline")}</span>
          <h2
            className="mt-5 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] font-light"
            data-testid="about-title"
          >
            {t("about.title_1")}<br />
            {t("about.title_2")}{" "}
            <em className="text-[#D4AF37] not-italic">{t("about.title_em")}</em>
          </h2>
          <p className="mt-8 text-white/75 text-base sm:text-lg font-light leading-relaxed max-w-2xl">
            {t("about.intro")}
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          {profiles.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.testid}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.15 }}
                className="card-lux p-8 sm:p-10"
                data-testid={p.testid}
              >
                <div className="flex items-center justify-between mb-8">
                  <Icon className="text-[#D4AF37]" size={26} strokeWidth={1.5} />
                  <span className="text-[10px] tracking-[0.32em] uppercase text-white/40">0{i + 1}</span>
                </div>
                <span className="overline">{p.role}</span>
                <h3 className="mt-3 font-serif-display text-3xl sm:text-4xl text-white leading-tight">
                  {p.name}
                </h3>
                <p className="mt-6 text-white/65 text-base font-light leading-relaxed">{p.text}</p>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {pillars.map((p, i) => (
            <motion.div
              key={p.no}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 * i }}
              className="border-t border-[#D4AF37]/30 pt-5"
              data-testid={`pillar-${p.no}`}
            >
              <span className="font-serif-display text-3xl text-[#D4AF37]">{p.no}</span>
              <h4 className="mt-2 text-white text-lg font-normal tracking-wide">{p.title}</h4>
              <p className="mt-2 text-white/55 text-sm font-light leading-relaxed">{p.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
